<script setup>
// 홈 데스크톱. 2026-09-22 에 작업대(/home2)에서 / 로 올렸다.
// 예전 칸 점프 방식은 HomeDesktopView.vue 에 남아 /legacy 에서 볼 수 있다.
// 모바일은 HomeMobileView 가 따로 그린다 — 이 화면은 데스크톱 전용이다.
//
// 1920 캔버스를 창 폭에 맞춰 통째로 줄인다(2026-09-25 되돌림). 아래 "배율" 참고.
//
// 고쳐 갈 방향 (2026-09-22 합의)
//   1) 자유 스크롤 — 휠을 가로채지 않는다.                                  ← 완료
//   2) 섹션은 제 높이 그대로 둔다. "한 화면에 다 보여야 한다"는 전제는 철회됐다.
//   3) 연출은 스크롤이 속도를 끈다(스크러빙). 다만 되감지 않는다 —
//      진행률을 최고치로 고정한다.                                          ← 프레임에 적용됨
//   4) 긴 연출만 sticky 로 붙든다. 연출을 다 볼 시간을 벌려는 것이다.
//   5) 1920 캔버스 통째 scale() 을 걷어내고 섹션마다 반응형으로 다시 짠다.
//      ← 2026-09-25 되돌림. 섹션마다 따로 맞추니 한 화면에 규칙이 다섯 개 섞였다.
//
// ── 배율: 1920 을 통째로 줄인다 ──────────────────────────────────────
//
// 피그마가 1920 으로 그려졌으니 1920 에서 100% 로 나오고, 창이 좁아지면 글자·그림·
// 여백·간격이 전부 같은 비율로 줄어야 한다. 넓어져도 커지지 않는다.
//
// 한때 이 배율을 걷어내고 섹션마다 따로 맞춘 적이 있다(2026-09-22). 이유는
// "1280 에서 16px 메뉴가 10.7px 로 보인다" 였다. 그런데 섹션마다 맞추다 보니
// 한 화면에 규칙이 다섯 개 섞였고(큰 글자만 0.80, 작은 글자는 고정, 세로 높이는
// 0.84~1.00, 그림은 0.80~1.00) 간격이 폭마다 달라졌다. 값을 하나씩 쫓는 한
// 계속 어긋나므로 배율 하나로 되돌린다(2026-09-25).
//
// transform 이 아니라 zoom 이다.
//   transform 은 sticky/fixed 의 기준 상자를 빼앗는다 — 거래소개의 붙는 이미지와
//   디졸브 배경이 둘 다 깨진다. zoom 은 그 크기로 다시 배치하므로 둘 다 살아 있고
//   글자도 흐려지지 않는다.
//
// 바닥이 있다. 768(모바일 경계)까지 순수 비례로 내려가면 16px 글자가 8px 이 된다.
// 1188(본문 칸 폭)에서 멈춘다 — 그 아래로는 좌우가 조금씩 잘리지만, 멈춘 뒤에도
// 모든 것이 같은 배율이라 비율은 끝까지 유지된다.
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  DIVIDER_DRAW_EASE,
  DIVIDER_DRAW_MS,
  DIVIDER_LEAD_PX,
  INTRO_LAST_FRAME,
  INTRO_START_DELAY_MS,
  introHoldAfterFrame,
} from '@/data/homeMotionTiming.js'

import { DESIGN_WIDTH } from '@/composables/useDesignScale.js'
import { HEADER_REAL, useFitHeight } from '@/composables/useFitHeight.js'

import SiteHeader from '@/components/common/SiteHeader.vue'

// 섹션 8개. 전부 반응형이다.
import StatementSection from '@/components/home2/StatementSection.vue'
import TradingSection from '@/components/home2/TradingSection.vue'
import FooterSection from '@/components/home2/FooterSection.vue'
import AppPromoSection from '@/components/home2/AppPromoSection.vue'
import MediaSection from '@/components/home2/MediaSection.vue'
import TrustSection from '@/components/home2/TrustSection.vue'
import HeroSection from '@/components/home2/HeroSection.vue'
import VisionSection from '@/components/home2/VisionSection.vue'
import ScrollCue from '@/components/home2/ScrollCue.vue'
import SceneBackdrop from '@/components/home2/SceneBackdrop.vue'
import imgYoutubeMark from '@/assets/figma/0d4a9a4cd6f3b0bfe9a1d3e92731cdc51cfc8731.svg'

// 홈만 배율을 되살렸다(2026-10-15 지시). 다른 화면은 배율 1 이다.
//
// 10-14 에 전부 배율 1 로 바꿨더니 홈이 깨졌다 - 섹션에 남는 세로가
// 841 -> 597 캔버스px 으로 줄어 미디어(1131)·거래소개(877)가 useFitHeight 의
// 바닥값 0.7 에 걸렸다. 슬롯이 한 화면을 넘기면서(+195 · +17) "한 화면 = 섹션 하나"
// 가 깨지고 연출 트리거가 전부 밀렸다.
//
// 그래서 **본문만** 1920 기준으로 되돌리고 **헤더는 배율 밖에 둔다**(아래 템플릿).
// 헤더는 어느 폭에서나 135 실제px · 글자 16px 그대로다.
// 1188 은 본문 칸 폭이라 그 아래로는 더 줄이지 않는다.
const SCENE_DESIGN_W = 1920
const SCENE_MIN_W = 1188
const ZOOM_FLOOR = SCENE_MIN_W / SCENE_DESIGN_W

const zoom = ref(1)
// 헤더는 배율 밖이라 실제 135px 다. 배율이 걸린 본문 안에서는 그만큼이
// 135/배율 설계px 을 가린다(1440 에서 180). 슬롯 자리 계산에 이 값을 쓴다.
const headCanvas = computed(() => Math.round(HEADER_REAL / (zoom.value || 1)))
const measureZoom = () => {
  // 창 폭을 쓴다. zoom 이 걸린 뒤의 요소 폭을 재면 자기 자신을 되먹는다.
  const w = window.innerWidth
  if (!w) return
  zoom.value = Math.min(1, Math.max(ZOOM_FLOOR, w / SCENE_DESIGN_W))
}

// frame = 그 섹션이 "들어왔다"고 보는 스토리보드 프레임 번호.
// 섹션 컴포넌트는 전부 step >= 자기 프레임 한 줄로 등장 여부를 판단한다.
const SECTIONS = [
  { key: 'hero', component: HeroSection, frame: INTRO_LAST_FRAME },
  // 아래 구분선이 화면에 들어오는 순간 글을 띄운다(2026-09-26 요청).
  //
  // 비율(enterLine)로도 맞출 수 있지만(0.46 쯤) 그 값은 이 섹션 높이(355)와
  // 신뢰 리듬(96)에서 나온 것이라 둘 중 하나만 바뀌어도 조용히 어긋난다.
  // 그래서 줄 자체를 본다 — 아래 readScroll 의 enterAtDivider.
  // 히어로 바로 다음이라 슬롯을 주지 않는다(2026-09-26 요청: 첫번째 섹션은 그대로).
  // 주면 히어로 아래로 여백이 통째로 벌어진다 - 2560 에서 624 였다.
  { key: 'statement', component: StatementSection, frame: 16, enterAtDivider: true },
  // 앞 섹션과 나누는 줄(2026-09-25 요청). 창 끝까지 긋고,
  // 색은 거래소개의 가로줄(#d9d9d9)보다 한 단계 흐리다.
  // 선은 리듬 한가운데(60)에 온다. 한때 리듬을 두 배(240)로 줘 선 위아래로 120 씩
  // 뒀는데, 그러면 이 경계만 보이는 간격이 240 이 됐다(2026-09-26).
  // gapBefore = 앞 섹션과의 사이를 이만큼 더 벌린다(설계 px, 2026-09-30 요청:
  // "1397x769 기준으로 지금보다 2/3 정도 더").
  //
  // 슬롯 상자(헤더 + 한 화면)는 그대로 두고 바깥 상자에 marginTop 으로만 준다 -
  // 상자 안에 넣으면 글이 한가운데에서 밀려난다. 구분선은 measureDividers 가
  // 앞뒤 글의 한가운데로 다시 잡으므로 따라온다.
  //
  // 1397x769 실측 - 스테이트먼트~신뢰 123 -> 211, 신뢰~거래소개 222 -> 368.
  //
  // 대가: 연출은 "글 아랫변이 창 바닥에 닿는 순간" 에 도는데, 그 앞의 빈 화면이
  // 벌린 만큼 그대로 늘어난다(거래소개 25 -> 170). 오늘 줄여 둔 것과 반대
  // 방향이라 여기를 더 키우면 빈 구간이 그만큼 더 길어진다.
  { key: 'trust', component: TrustSection, frame: 21, slot: true, divider: true, gapBefore: 120 },
  // 신뢰와 나누는 줄. 스테이트먼트-신뢰 사이의 것과 같다(2026-09-27 요청).
  // 거래소개만 스크롤 포인트가 둘이다(2026-10-01 클라이언트 피드백).
  //   frame  30  뱃지 + 제목      글 아랫변이 창 바닥에 닿을 때 (다른 섹션과 같다)
  //   frame2 31  본문 + 인증      거기서 SLOT_STAGE2 만큼 더 내려갔을 때
  // 오른쪽 글이 넷 중 제일 길어(776) 한 번에 다 올리면 산만하다는 지적이었다.
  // stage1Top — 이 섹션만 두 점을 달리 잡는다(2026-10-16 요청).
  // 기본 규칙은 1단계가 "글 아랫변이 창 바닥" 이라 제목이 섹션이 다 들어온 뒤에야
  // 떴다. 제목은 섹션이 아래에서 올라올 때 이미 떠 있어야 한다는 요청이다.
  //   1단계(frame 30)  글 윗변이 창의 70% 까지 올라오면   -> 뱃지 + 제목
  //   2단계(frame2 31) 글 아랫변이 창 바닥에 닿으면       -> 본문 · 구분선 · 인증
  { key: 'trading', component: TradingSection, slot: true, divider: true, frame: 30, frame2: 31, gapBefore: 200, stage1Top: 0.7 },
  { key: 'media', component: MediaSection, slot: true, frame: 33 },
  // 한때 이 섹션만 fill 로 한 화면을 채웠다(2026-09-26). 핸드폰을 줄이면서 내용이
  // 619 가 되어 배경이 진해지는 자리에서 위로 쏠려 보였기 때문이다.
  //
  // 같은 날 걷었다. fill 은 빈자리가 "화면높이 - 내용" 이라 배율이 아니라 화면
  // 높이를 그대로 따라간다 - 다른 섹션 여백이 1.25 배 커질 때 이것만 1.88 배가 됐고
  // 세로 1440 모니터에서 내용(619)보다 빈자리(701)가 더 컸다.
  // 이제 다른 섹션과 같은 리듬 120 이다.
  { key: 'app', component: AppPromoSection, slot: true, frame: 37 },
  // 히어로와 비전은 슬롯을 주지 않는다(2026-09-26 요청: "첫번째랑 마지막 비전은 다르다").
  // 비전은 리듬 120 으로 푸터 바로 앞에 붙고, 연출도 푸터 라인이 보이는 순간에 돈다
  // (enterBottom). 슬롯을 주면 콘텐츠 아랫변과 푸터 라인이 200 넘게 떨어져
  // 그 의도가 깨진다.
  { key: 'vision', component: VisionSection, frame: 42, enterBottom: true },
  // 푸터의 어두운 띠는 배경이라 바깥 상자가 그린다(2026-09-26: 배경과 콘텐츠를 나눴다).
  // 띠는 비전이 끝나는 자리에서 시작해 페이지 끝까지 가고, 그 안에서 글이 리듬 120
  // 만큼 내려와 앉는다 - 그래서 보이는 간격이 다른 경계와 같아진다. 한때 리듬이 0
  // 이었는데, 그때는 띠의 제 여백(50)만 남아 이 경계만 51 이었다.
  // 디졸브(SceneBackdrop)에 넣지 않는다. 거기는 투명도로 섞는 층이라 색이 서서히
  // 번지는데, 푸터는 색이 딱 바뀌는 경계다.
  { key: 'footer', component: FooterSection, frame: 48, band: '#252525', bandBottom: 100 },
]
const STEPPED = new Set(['hero', 'statement', 'trust', 'trading', 'media', 'app', 'vision'])

// ── 섹션 하나가 한 화면 ──────────────────────────────────────────────
//
// 섹션마다 헤더 아래 한 화면을 주고, 내용이 그보다 크면 그만큼만 더 줄인다.
// 원본 디자인이 헤더를 포함한 한 화면을 전제로 그려지지 않아서 필요한 보정이다 —
// 설계 1080 에서 헤더 140 을 빼면 940 인데 신뢰 1010 · 거래소개 974 · 비전 1116 이다.
// 글자나 간격을 따로 손대지 않고 통째로 줄이므로 디자인 비율은 그대로다.
//
// 히어로는 뺀다. 헤더가 그 위에 얹히는 것이 원본 그대로이고, 이미 화면을 채운다.
const fitEls = SECTIONS.map(() => ref(null))
// 히어로만 조건이 다르다. 헤더가 fixed 로 그 위에 겹치므로 창 전체를 쓰고(headroom 0),
// 카드 휠이 원래 잘리도록 그려진 그림이라 바닥에 자리를 남기지 않는다(breath 0).
const fits = fitEls.map((el, i) =>
  useFitHeight(el, SECTIONS[i].key === 'hero' ? { headroom: 0, breath: 0 } : {}).fit,
)
// v-for 안에서 ref 객체를 그대로 바인딩하면 Vue 가 배열로 넣는다. 함수로 받아 담는다.
const setFitEl = (el, i) => {
  fitEls[i].value = el || null
}
// 배열 안의 ref 는 템플릿에서 자동으로 풀리지 않는다. 풀어서 넘긴다 —
// 안 그러면 zoom 에 객체가 들어가 조용히 무시된다.
const fitZoom = computed(() => fits.map((f) => f.value))
// 섹션 사이 여백. 위에만 준다(아래는 0) — 그래야 섹션마다 첫 줄이 같은 자리에서
// 시작한다. 현대차가 140/0, 카카오뱅크가 80 계열, 애플이 56/63 으로 같은 방식이다.
// 우리 설계 px 는 1536 창에서 실제 px 의 1.25 배이므로 120 은 실제 96px 이다.
const RHYTHM = 120

// slot: true 인 섹션은 두 가지를 같이 받는다.
//   배치    화면 하나를 받고 그 안에서 가운데 (rhythmBox)
//   연출    콘텐츠 한가운데가 볼 수 있는 자리의 한가운데에 닿을 때 (readScroll)
// 둘이 한 짝이다 - 한 화면을 차지하는 섹션은 한가운데 왔을 때 도는 게 맞다.
//
// 슬롯 높이의 상한(설계 px). 1440 모니터에서 나오는 값이다 - 사용자 환경에서
// innerHeight 가 1439 로 나왔고(2026-09-26 실측), 거기서 헤더 134 를 뺐다.
//
// 왜 상한이 필요한가: 화면이 높을수록 남는 자리가 무한정 늘어난다. 4K 에서
// 스테이트먼트를 가운데 두면 위아래가 700 가까이 비는데 그건 배치가 아니라 빈 화면이다.
// 대신 1440 을 넘으면 슬롯보다 화면이 커져서 이웃이 다시 보인다 - 1440 까지만 보장한다.
//
// 한 화면 = 1920 x (창높이 / 창폭) - 134 이다. 배율이 폭에서 나오므로 결국
// 창의 가로세로 비율 하나로 정해진다 - 폭이 달라도 비율이 같으면 똑같이 보인다.
//
// 2026-09-30 정정. 전에는 여기에 "1366x768 은 945, 1920x1080 은 946" 이라고
// 적어 두었는데, 그건 모니터 해상도를 창 높이로 그대로 쓴 값이다. 실제 브라우저는
// 탭바·주소창이 70~100 을 가져간다.
//
//   모니터        실제 창        비율    한 화면
//   1366x768     1366x698      1.96     847
//   1920x1080    1920x981      1.96     847
//   (창을 그대로 768/1080 으로 보면)             945 / 946
//
// 흔한 노트북 두 종류가 둘 다 847 로 모인다. 945 는 브라우저 크롬이 없는 값이라
// 기준으로 삼으면 안 된다 - 실제보다 한 화면을 100 가까이 크게 잡는다.
// 노트북 화면을 재현할 때는 Responsively 에 1366x698 을 넣는다(2026-09-30 확인:
// 캡처에서 역산한 비율 1.96 과 일치).
//
// 혼자 튀는 건 큰 모니터뿐이다(1305). 1920 을 넘으면 배율이 1 에서 멈추기 때문이다.
const SLOT_MAX = 1305

// 콘텐츠 윗변이 헤더에서 이만큼까지는 떨어져 있어도 된다(설계 px).
//
// 슬롯 섹션은 2026-09-30 부터 이 값을 쓰지 않는다 — 연출 시점이 "글 아랫변이 창
// 바닥에 닿는 순간" 하나로 정리됐다(경위는 readScroll 의 slot 갈래).
// 지금 남아 있는 쓰임은 readBackdrop 의 **슬롯이 아닌** 경계 둘뿐이다 —
// 스테이트먼트(enterAtDivider)와 비전(enterBottom). 그 둘은 제 트리거가 따로라
// 배경이 정확히 맞춰지지 않고, 예전부터 쓰던 어림값을 그대로 둔 것이다.
//
// 값 자체는 같은 날 200 -> 120 으로 내렸다. 창 517개(폭 1188~1920 x 높이 560~1020)로
// 훑어 보니 200 은 어긋남이 최악 99px 이고 120 은 59px 이었다.
const SLOT_ENTER_TOP_MAX = 120

// 스크롤 포인트가 둘인 섹션에서, 두 번째가 첫 번째보다 이만큼 아래다(설계 px).
// 거래소개에만 쓴다. 200 은 노트북(1440x731)에서 실제 150px 이라 휠 한 칸 반이다 -
// 한 칸이면 눈치채기 전에 지나가고, 두 칸이면 글이 헤더에 많이 잘린다.
const SLOT_STAGE2 = 200

// 스크롤 스냅은 쓰지 않는다.
//
// 슬롯만으로는 "한 화면에 하나" 가 가능해질 뿐 항상 그렇지는 않아서, 스크롤을 멈출 때
// 가까운 슬롯에 붙는 scroll-snap(proximity)을 얹어 봤다. 걷었다 - 멈추는 순간 브라우저가
// 알아서 끌어당기는 게 "그냥 쑥 올라간다" 로 느껴진다(2026-09-26 제보).
// 휠 이동량을 직접 바꾸는 안도 같은 이유로 안 한다. 자유 스크롤로 둔다.

// 구분선이 있는 섹션은 리듬을 두 배로 갖고 선을 그 한가운데 둔다.
// 선을 섹션 안에 두면 줄임 상자 안이라 내용 높이로 계산돼 배율이 떨어진다
// (신뢰 899 -> 1019, 1.000 -> 0.893). 그래서 바깥 상자가 그린다.
//
// 상자는 `헤더 + 한 화면` 이다. 2026-09-30 에 헤더 몫을 빼는 안을 재 봤는데
// (빈 화면 0 · 잘림 0 이 된다) 걷었다 — "한 화면 = 섹션 하나" 가 클라이언트
// 요구사항이라 섹션이 창을 통째로 차지해야 한다. 그 제약에서 나오는 결론은
// readScroll 의 slot 갈래에 적었다 — 빈 화면과 잘림의 합이 헤더 높이로 고정이다.
const rhythmBox = (section) =>
  section.slot
    ? {
        // 헤더 아래 한 화면을 주고 그 안에서 가운데. paddingTop 이 헤더만큼
        // 있어야 가려지는 자리를 빼고 가운데가 된다.
        // "한 화면" = 창 높이 / 배율 - 134. 시세표를 펼쳐 헤더가 커지는 건 배제한다
        // (2026-09-26 사용자 판단: 그것까지 넣으면 정리가 안 된다).
        minHeight: `calc(${headCanvas.value}px + min(100vh / var(--z, 1) - ${headCanvas.value}px, ${SLOT_MAX}px))`,
        paddingTop: `${headCanvas.value}px`,
        justifyContent: 'center',
      }
    : {
        paddingTop: `${section.rhythm ?? RHYTHM}px`,
        // 띠가 있는 섹션은 아래 여유도 바깥 상자가 갖는다. 띠를 아래로 삐져나가게
        // 하면 문서 높이가 늘어 빈 스크롤이 생긴다.
        ...(section.band ? { paddingBottom: `${section.bandBottom ?? 0}px` } : {}),
      }

// ── 디졸브 배경 ──────────────────────────────────────────────────────
//
// 섹션들 뒤에 화면에 붙박인 배경 층을 깔고, 스크롤에 따라 색을 겹쳐 바꾼다.
// 그래서 이 섹션들은 제 배경을 끄고 나온다(bare). 자세한 이유는 SceneBackdrop.vue 에 있다.
//
// 앞뒤로 빠지는 섹션이 있다. 둘은 제 배경을 그대로 갖는다.
//   히어로  아래쪽이 카드 휠이라 배경을 건드리지 않기로 했다.
//   푸터    비전 → 푸터 구간은 디졸브에서 빼 달라고 했다.
// 그래서 스테이트먼트(SECTIONS 의 1번)부터 비전(6번)까지가 짝이고,
// 비전 → 푸터 경계에서는 색이 섞이지 않고 그대로 바뀐다.
//
// (한 번 비전까지 같이 뺐다가 되돌렸다. 비전을 빼면 앱 → 비전 디졸브까지 같이 죽는다.
//  빼 달라고 한 것은 비전 자체가 아니라 비전에서 푸터로 넘어가는 구간이었다.)
const FIRST_BACKDROP = 1
const BACKDROPS = [
  { key: 'statement', css: '#ffffff' },
  { key: 'trust', css: '#fdfbf8' },
  { key: 'trading', css: '#fdfbf8' },
  // 회색 유튜브 워터마크. 원본은 캔버스(925) 대비 로고 세로(872)가 94% 였고
  // 회전 후 상자 중심이 폭의 28% 였다. 그 비율을 화면 기준으로 옮긴 값이다.
  {
    key: 'media',
    css: '#151512',
    art: { src: imgYoutubeMark, height: '94%', ratio: '1291.83 / 898.351', left: '28%', rotate: '-30deg' },
  },
  // 앱만 그라데이션이다. 층을 겹쳐 opacity 로 섞으므로 단색과 똑같이 다뤄진다.
  { key: 'app', css: 'linear-gradient(119.018deg, #000000 1.133%, #221602 174.35%)' },
  // 곡선 4개. 비전 섹션 안에 있던 장식인데, 콘텐츠 상자에 잘리고 구조 층과
  // 배율이 달라 카드와 따로 놀았다(2026-09-26: 배경과 콘텐츠를 나눴다).
  // 곡선은 2026-10-16 에 비전 섹션으로 되돌렸다(VisionSection 의 주석 참고) -
  // 배경 층과 배율이 1.21배 달라 하이라이트 점이 차트 라인에서 벗어났기 때문이다.
  // 여기는 색만 남는다. SceneBackdrop 의 decoFrame 기능은 그대로 두었다.
  { key: 'vision', css: '#fffdfa' },
]
const LAST_BACKDROP = FIRST_BACKDROP + BACKDROPS.length - 1

// 섹션 윗변이 화면 한가운데까지 올라오면 그 섹션에 들어온 것으로 본다.
// 그 순간 화면 아래 절반이 그 섹션이다 — 읽을 만큼 보이고 나서 연출이 시작된다.
//
// 처음에는 0.8(화면 아래에서 20% 지점)로 뒀는데, 섹션이 바닥에서 빼꼼 올라오자마자
// 연출이 시작돼 화면 밖에서 절반쯤 끝나 버렸다.
const ENTER_LINE = 0.5

// ── 폭이 바뀌면 다시 읽는다 ──────────────────────────────────────────
//
// 배율은 없다. 다만 폭이 바뀌면 섹션들이 다시 흐르면서 높이가 달라지고,
// 그러면 어느 섹션이 화면에 들어왔는지도 달라진다. 그때 스크롤을 다시 읽는다.
const viewport = ref(null)
let observer = null
let lastW = 0

const measure = () => {
  const width = viewport.value?.clientWidth
  if (!width || width === lastW) return
  lastW = width
  onScroll()
}

// ── 프레임 ───────────────────────────────────────────────────────────
//
// 인트로가 1 → 5 까지 올리고, 그 다음부터는 스크롤이 올린다.
//
// 한 번 올라간 프레임은 내려가지 않는다(합의 3번). 위로 올렸다 다시 내려도
// 연출이 처음부터 다시 돌지 않고 끝난 상태 그대로 있다 —
// 예전 방식의 가장 큰 불만이 "올렸다 내리면 5초를 또 기다린다"였다.
const frame = ref(1)
const lift = (n) => {
  if (n > frame.value) frame.value = n
}

let introTimer = null
let introDone = false

// 스크롤과 무관하게 로딩 후 한 번 재생된다.
// 1 검정 → 2 배경 → 3 공용 상단 → 4 히어로 글 → 5 카드 휠.
const playIntro = () => {
  const advance = (n) => {
    lift(n)
    if (n >= INTRO_LAST_FRAME) {
      introDone = true
      onScroll()
      return
    }
    introTimer = setTimeout(() => advance(n + 1), introHoldAfterFrame(n))
  }
  introTimer = setTimeout(() => advance(2), INTRO_START_DELAY_MS)
}

// 섹션 위치는 DOM 에서 직접 잰다. flow 섹션은 제 높이를 스스로 가져서
// 좌표표로는 어디에 있는지 알 수 없기 때문이다.
const sectionEls = ref([])
const setSectionEl = (el, i) => {
  if (el) sectionEls.value[i] = el
}

// 지금 스크롤 위치에서 "들어왔다"고 볼 수 있는 가장 뒤쪽 섹션의 프레임.
// 인트로가 도는 동안에는 손대지 않는다 — 맨 위에서도 히어로가 이미 5번이라
// 켜 두면 인트로를 건너뛰어 버린다.
const readScroll = () => {
  if (!introDone) return
  // 바닥에 닿았으면 더 내려갈 데가 없으므로 화면 아래끝을 기준으로 본다.
  // 마지막 섹션(푸터)은 제 윗변이 화면 한가운데까지 올라올 만큼 스크롤할 수가 없어서,
  // 가운데 기준만 쓰면 영영 켜지지 않는다.
  const doc = document.documentElement
  const atBottom = window.scrollY + window.innerHeight >= doc.scrollHeight - 2
  let reached = INTRO_LAST_FRAME
  SECTIONS.forEach((section, i) => {
    const el = sectionEls.value[i]
    // 두 단계짜리 섹션은 마지막 프레임까지 와야 넘어간다(frame2).
    if (!el || (section.frame2 ?? section.frame) <= reached) return
    // enterAtDivider: 다음 섹션과 나누는 줄이 창 안에 들어왔을 때.
    //
    // 줄이 보이면 이 섹션은 이미 통째로 화면에 있다는 뜻이다. 비율로 재면
    // 섹션 높이나 리듬이 바뀔 때 조용히 어긋나므로 줄 자체를 본다.
    // 줄은 다음 섹션 상자가 그린다(rhythmBox 의 divider).
    if (section.enterAtDivider) {
      const mark = sectionEls.value[i + 1]?.querySelector('[data-divider]')
      if (atBottom || (mark && mark.getBoundingClientRect().top <= window.innerHeight)) {
        reached = section.frame
      }
      return
    }
    // enterBottom: 푸터 라인(어두운 띠 윗변)이 창에 들어온 순간(2026-09-26 요청).
    //
    // 통째로 보일 때로 재면 안 된다. 비전은 내용이 볼 수 있는 자리를 거의 채워서
    // 통째로 보이는 자리가 좁은데, 스크롤 한 칸이 100px 이라 건너뛴다.
    // 윗변이 들어왔는가로 재면 지나친 뒤에도 참이라 놓치지 않는다.
    //
    // 한때 비전 콘텐츠 아랫변을 봤다. 슬롯을 준 뒤로는 그 아랫변과 푸터 라인이
    // 200 가까이 떨어져서, 라인이 보이기 한참 전에 연출이 돌았다.
    if (section.enterBottom) {
      const mark = sectionEls.value[i + 1]?.querySelector('[data-band]')
      const node = mark ?? fitEls[i].value
      const line = mark
        ? node?.getBoundingClientRect().top
        : node?.getBoundingClientRect().bottom
      if (atBottom || (line != null && line <= window.innerHeight)) {
        reached = section.frame
      }
      return
    }
    // slot: 글 아랫변이 창 바닥에 닿는 순간 (2026-09-30 선택).
    //
    // 상자가 창 한 장이고("한 화면 = 섹션 하나", 클라이언트 요구사항) 글은 그
    // 안에서 한가운데다. 그래서 연출 시점 T 를 어디에 두든 이렇게 된다 —
    //
    //   빈 화면 = (앞 글이 헤더 밑으로 사라지는 자리) - T
    //   잘림    = T + 글 높이 - 창 높이
    //   ────────────────────────────────────────────────
    //   합      = 헤더 + (이 글 - 앞 글) / 2      <- T 가 없다. 상수다.
    //
    // 즉 둘을 동시에 0 으로 만들 수 없고 배분만 고를 수 있다. 1440x731 실측
    // (헤더 101) — 거래소개 38 · 미디어 104 · 앱 67. 1920x981 은 48 · 138 · 89.
    // 앞 섹션과 키가 같은 미디어가 늘 제일 나쁘다.
    //
    // 잘림 쪽을 0 으로 두기로 했다(2026-09-30). 잘린 제목은 버그로 읽히고
    // 빈 여백은 호흡으로 읽힌다. 그 자리가 곧 "글이 화면에 다 들어온 순간" 이라
    // 규칙이 한 줄로 줄어든다.
    //
    // 이전 규칙(max(slack/2, min(slack, 120)))은 그 최적점도 아니었다 —
    // 잘림 0 을 지키면서 빈 화면이 150~232 였다. 이제 38~104 다.
    //
    // 아는 대가: 창이 1439 설계 px 을 넘는 큰 모니터에서는 글이 화면 아래쪽에
    // 붙은 채로 연출이 시작된다(2026-09-27 에 이 이유로 물렸던 자리다.
    // 2000x1361 에서 위 465 / 아래 0). 사용자가 알고 고른 것이다.
    //
    // 상자가 아니라 콘텐츠를 본다(fitEls). 슬롯 상자는 남는 자리까지 품고 있다.
    // 신뢰는 머리말이 제 트리거(headReady)를 따로 가지고 있어 여기와 무관하다.
    if (section.slot) {
      const node = fitEls[i].value
      if (!node) return
      const box = node.getBoundingClientRect()
      const bottom = box.bottom

      // stage1Top 이 있으면 두 점을 다르게 잡는다(2026-10-16, 거래소개).
      //   1단계  글 윗변이 창의 stage1Top 지점까지 올라오면 -> 머리말(뱃지 + 제목)
      //   2단계  글 아랫변이 창 바닥에 닿으면               -> 그 아래 전부
      // 아래 기본 규칙과 달리 SLOT_STAGE2 를 쓰지 않는다 - 2단계가 곧 기본의 1단계다.
      if (section.stage1Top != null) {
        if (section.frame2 && (atBottom || bottom <= window.innerHeight)) {
          reached = section.frame2
          return
        }
        if (atBottom || box.top <= window.innerHeight * section.stage1Top) {
          reached = section.frame
        }
        return
      }
      // 두 번째 포인트. 첫 포인트에서 SLOT_STAGE2(설계 px)만큼 더 내려가면 켠다.
      // 그 사이 글은 아직 거의 다 보인다 - 글(776)과 볼 수 있는 자리(841)의 차이가
      // 65 뿐이라, 200 을 더 내려가면 위쪽 135(뱃지 언저리)만 헤더에 가린다.
      if (section.frame2 && (atBottom || bottom <= window.innerHeight - SLOT_STAGE2 * (zoom.value || 1))) {
        reached = section.frame2
        return
      }
      if (atBottom || bottom <= window.innerHeight) {
        reached = section.frame
      }
      return
    }
    // 섹션마다 기준선을 달리 둘 수 있다. 안 주면 화면 한가운데(ENTER_LINE)다.
    const enter = atBottom ? 1 : (section.enterLine ?? ENTER_LINE)
    const line = window.scrollY + window.innerHeight * enter
    if (el.getBoundingClientRect().top + window.scrollY <= line) reached = section.frame
  })
  lift(reached)
}

// 지금 몇 번째 배경에 있는가. 1.5 면 1번과 2번이 반반 겹친 상태다.
//
// 경계마다 "지났는가"를 0~1 로 재서 더한다. 경계가 화면 한가운데를 지나는 순간이 0.5 이고,
// 그 앞뒤로 화면 한 장 동안 서서히 넘어간다 — 연출 시작 기준(ENTER_LINE)과 같은 자리다.
// 더해서 쓰므로 섹션이 화면보다 짧아 두 경계가 겹쳐도 값이 엉키지 않는다.
//
// 연출 프레임과 달리 이 값은 고정하지 않는다. 배경은 연출이 아니라
// "지금 어디에 있는가"라서 위로 올리면 그 자리 색으로 돌아와야 한다.
const bgMix = ref(0)
const readBackdrop = () => {
  const vh = window.innerHeight
  const z = zoom.value || 1
  // 헤더는 배율 밖이라 실제 px 그대로다(2026-10-15). 아래 vh 와 단위가 맞는다.
  const head = HEADER_REAL
  // 마지막 경계(비전)는 그 뒤에 푸터밖에 없어서, 창이 아주 높으면 기준선이 비전 안으로
  // 충분히 들어오기 전에 스크롤이 멈춘다. 페이지 끝 한 화면 동안 기준선을
  // 아래끝으로 미끄러뜨려 마지막 색이 온전히 나오게 한다.
  const maxScroll = Math.max(0, document.documentElement.scrollHeight - vh)
  const tail = Math.min(1, Math.max(0, (window.scrollY - (maxScroll - vh)) / vh))

  let mix = 0
  for (let i = FIRST_BACKDROP + 1; i <= LAST_BACKDROP; i += 1) {
    // 상자가 아니라 콘텐츠를 본다(fitEls). 슬롯 상자는 남는 자리까지 품고 있어서
    // 그 윗변은 콘텐츠와 무관하게 움직인다 - 그래서 배경이 연출보다 늦었다
    // (2026-09-27 제보: 1366x600 에서 거래소개 116px, 미디어 113px).
    const here = fitEls[i].value
    const prev = fitEls[i - 1]?.value
    if (!here || !prev) continue
    const r = here.getBoundingClientRect()
    // 연출이 도는 순간 콘텐츠 윗변이 서는 자리. readScroll 과 같은 식이라야
    // 글이 뜨는 그때 색이 다 바뀌어 있다.
    //   슬롯    글 아랫변이 창 바닥 -> 윗변은 창높이 - 글높이
    //   그 밖   스테이트먼트·비전. 제 트리거가 따로라 예전 어림값을 그대로 둔다.
    const slack = (vh - head - r.height) / z
    const enterTop = Math.max(slack / 2, Math.min(slack, SLOT_ENTER_TOP_MAX))
    const base = SECTIONS[i].slot ? vh - r.height : head + enterTop * z
    const target = base + (vh - base) * tail
    // 섞이는 거리. 화면 한 장이 기본이지만 이웃 콘텐츠가 그보다 짧으면 그 높이에
    // 맞춘다 - 화면보다 짧은 콘텐츠는 제 색을 온전히 가질 수가 없기 때문이다.
    const band = Math.max(1, Math.min(vh, prev.getBoundingClientRect().height, r.height))
    // +1 이라 콘텐츠 윗변이 그 자리에 닿는 순간 1 이 된다 = 연출이 도는 바로 그때
    // 색이 다 바뀌어 있다. 그 한 band 앞에서 0 이다.
    mix += Math.min(1, Math.max(0, (target - r.top) / band + 1))
  }
  bgMix.value = mix
}

// 구분선이 펼쳐졌는가. 한 번 켜면 되돌리지 않는다(프레임의 lift 와 같은 약속이다 -
// 위로 올렸다 내려도 다시 그리지 않는다).
//
// 줄이 창에 들어오고 한 칸(DIVIDER_LEAD_PX) 더 내려갔을 때 켠다.
//
// 세 번 옮겼다. 창 아랫변에 닿자마자는 화면 맨 아래 가장자리에서 도는 셈이라 안
// 보였고(게다가 그 순간이 스테이트먼트의 enterAtDivider 와 같은 지점이라 앞 연출에
// 묻혔다), 볼 수 있는 자리 한가운데는 너무 늦었다(2026-09-27).
// 앞 섹션 연출이 끝나고 한 번 더 굴렸을 때 줄이 그어지는 자리다.
// 구분선의 자리. 앞 섹션 콘텐츠 아랫변과 이 섹션 콘텐츠 윗변의 한가운데다.
//
// 리듬 한가운데(60)로 고정해 뒀더니 위아래가 달랐다(2026-09-27 제보: 신뢰-거래소개가
// 위 76 아래 157). 그 값은 앞 섹션이 제 상자에 꽉 찰 때만 맞는데, 슬롯 섹션은
// 콘텐츠를 가운데 두므로 위아래로 남는 자리가 생긴다.
// 남는 자리는 창 높이를 타서 CSS 로는 못 구한다. 그래서 잰다.
const dividerTop = ref({})
const measureDividers = () => {
  const z = zoom.value || 1
  const next = {}
  SECTIONS.forEach((section, i) => {
    if (!section.divider) return
    const box = sectionEls.value[i]?.firstElementChild
    const here = fitEls[i].value
    const prev = fitEls[i - 1]?.value
    if (!box || !here || !prev) return
    const top = box.getBoundingClientRect().top
    const above = prev.getBoundingClientRect().bottom
    const below = here.getBoundingClientRect().top
    next[section.key] = Math.round(((above + below) / 2 - top) / z)
  })
  dividerTop.value = next
}

// 줄임(fit)이 정해진 뒤에 재야 한다. 그 값이 콘텐츠 높이를 바꾸고, 높이가
// 남는 자리를 바꾼다.
const onResizeDividers = () => nextTick(measureDividers)
watch(fitZoom, onResizeDividers)

const dividerOpen = ref({})
const readDividers = () => {
  SECTIONS.forEach((section, i) => {
    if (!section.divider || dividerOpen.value[section.key]) return
    const mark = sectionEls.value[i]?.querySelector('[data-divider]')
    if (!mark) return
    if (mark.getBoundingClientRect().top <= window.innerHeight - DIVIDER_LEAD_PX) {
      dividerOpen.value = { ...dividerOpen.value, [section.key]: true }
    }
  })
}

const onScroll = () => {
  readScroll()
  readBackdrop()
  readDividers()
}

// 섹션에 내려 줄 속성. 디졸브에 참여하는 섹션만 제 배경을 끄고 나온다 —
// 히어로·비전·푸터는 그대로 제 배경을 갖는다.
const propsFor = (section, index) => ({
  ...(STEPPED.has(section.key) ? { step: frame.value } : {}),
  ...(index >= FIRST_BACKDROP && index <= LAST_BACKDROP ? { bare: true } : {}),
})

// ── 시세표를 펼쳤을 때 ──────────────────────────────────────────────
//
// 헤더를 fixed 로 띄워 히어로 위에 얹어 두기 때문에, 헤더가 커져도 본문은 가만히
// 있는다. 흐름 안에 헤더를 두는 미디어허브·자사몰과 달리 여기서만 따로 밀어 줘야
// 한다(2026-09-26 요청: 레이어로 덮지 말고 본문을 밀 것).
//
// 표 높이는 행 수와 글꼴에 달려 있어 미리 알 수 없다. 그래서 헤더를 재고,
// 평소 높이(140 설계 px)를 넘는 만큼만 본문을 내린다.
const headerEl = ref(null)
const headerGrow = ref(0)

const measureHeader = () => {
  const node = headerEl.value
  if (!node) return
  const z = zoom.value || 1
  // 헤더가 배율 밖이라 rect 가 실제 px 다. 커진 만큼(실제)을 설계 px 로 바꿔
  // 본문 상자의 paddingTop 에 쓴다.
  headerGrow.value = Math.max(
    0,
    Math.round((node.getBoundingClientRect().height - HEADER_REAL) / z),
  )
}

// 헤더가 시세표를 펼치거나 접을 때 알려 준다. 그려진 뒤에 재야 새 높이가 나온다.
const onRatesToggle = () => nextTick(measureHeader)

let headerObserver = null
onMounted(() => {
  measureHeader()
  nextTick(measureDividers)
  if (!headerEl.value) return
  // 글꼴이 늦게 와서 표 높이가 달라지는 경우까지 잡는다.
  headerObserver = new ResizeObserver(measureHeader)
  headerObserver.observe(headerEl.value)
  window.addEventListener('resize', measureHeader)
  window.addEventListener('resize', onResizeDividers)
})
onBeforeUnmount(() => {
  headerObserver?.disconnect()
  headerObserver = null
  window.removeEventListener('resize', measureHeader)
  window.removeEventListener('resize', onResizeDividers)
})

// 공통 상단은 인트로 3번째 칸에서 위에서 내려온다.
const headerReveal = computed(() => {
  const on = frame.value >= 3
  return {
    transform: on ? 'translateY(0)' : 'translateY(-100%)',
    opacity: on ? 1 : 0,
    transitionDuration: '700ms',
  }
})

onMounted(() => {
  measureZoom()
  measure()
  observer = new ResizeObserver(measure)
  observer.observe(viewport.value)
  window.addEventListener('resize', measure)
  window.addEventListener('resize', measureZoom)
  window.addEventListener('scroll', onScroll, { passive: true })
  readBackdrop()
  playIntro()
})

onBeforeUnmount(() => {
  clearTimeout(introTimer)
  observer?.disconnect()
  observer = null
  window.removeEventListener('resize', measure)
  window.removeEventListener('resize', measureZoom)
  window.removeEventListener('scroll', onScroll)
})
</script>

<template>
  <!-- 여기에 overflow 를 주면 안 된다 — 그 순간 이 요소가 스크롤 컨테이너가 되어
       거래소개의 position: sticky 가 창이 아니라 이 상자를 기준으로 잡힌다. -->
  <div
    ref="viewport"
    class="relative w-full bg-white"
    :style="{ zoom, '--z': zoom, paddingTop: `${headerGrow}px` }"
  >
    <!-- 섹션 배경. 화면에 붙박여 있고 스크롤에 따라 색이 겹쳐 바뀐다(디졸브).
         섹션들은 제 배경을 끄고 나오므로(bare) 이 층이 비쳐 보인다. -->
    <!-- offset 은 헤더가 시세표를 펼쳐 커진 높이다. 바깥 상자의 paddingTop 과
         같은 값인데, 이 층은 fixed 라 그 padding 을 안 따라가서 따로 넘겨 준다.
         이걸 안 주면 본문만 내려가고 배경은 그대로라, 펼친 표가 히어로 위에
         얹힌 레이어로 보인다(2026-09-29 지적). -->
    <SceneBackdrop :layers="BACKDROPS" :mix="bgMix" :zoom="zoom" :offset="headerGrow" :step="frame" />

    <template v-for="(section, i) in SECTIONS" :key="section.key">
      <!-- 섹션은 제 높이를 스스로 갖는다. 배율은 걸리지 않는다. -->
      <!-- gapBefore 가 있으면 앞 섹션과의 사이를 그만큼 더 벌린다.
           바깥 상자에 준다 - 슬롯 상자 안에 넣으면 글이 한가운데에서 밀려난다. -->
      <div
        :ref="(el) => setSectionEl(el, i)"
        class="relative"
        :style="section.gapBefore ? { marginTop: `${section.gapBefore}px` } : null"
      >
        <!-- 히어로는 리듬 상자 없이 바로 온다(헤더가 fixed 로 그 위에 얹힌다).
             줄임(fit)은 다른 섹션과 똑같이 걸린다 - 2026-09-26 이전에는 여기만 규칙
             밖이라 높이가 원본 캔버스(1009)로 고정이었고, 창이 낮으면 넘쳤다
             (1366x768 에서 118 이 창 밖). -->
        <div
          v-if="section.key === 'hero'"
          :ref="(el) => setFitEl(el, i)"
          :style="{ zoom: fitZoom[i], '--fit': fitZoom[i] }"
        >
          <component :is="section.component" v-bind="propsFor(section, i)" />
        </div>
        <!-- 나머지는 헤더 아래 한 화면 상자에 담고, 넘치면 그만큼만 줄인다. -->
        <div v-else class="relative flex flex-col" :style="rhythmBox(section)">
          <!-- 어두운 띠(푸터). 배경이라 콘텐츠 상자 밖, 리듬까지 덮는 이 층이 그린다.
               색이 딱 바뀌는 경계라 디졸브 층에 넣지 않는다. -->
          <div
            v-if="section.band"
            aria-hidden="true"
            data-band
            class="absolute inset-0"
            :style="{ background: section.band }"
          />
          <!-- 섹션을 나누는 줄. 창에 들어오면 가운데에서 양끝으로 펼쳐진다
               (2026-09-27 요청). 폭이 아니라 scaleX 를 움직인다 - 폭을 애니메이션하면
               매 프레임 레이아웃을 다시 잡는다.
               속도와 곡선은 글과 다른 값을 쓴다(DIVIDER_DRAW_*). 왜 다른지는 거기 적었다.
               data-divider 는 앞 섹션이 이 줄을 찾아 연출 시점을 잡는 표시다
               (enterAtDivider). scaleX 는 top 을 바꾸지 않아 그 계산에 영향이 없다. -->
          <div
            v-if="section.divider"
            aria-hidden="true"
            data-divider
            class="absolute left-0 h-px w-full origin-center bg-[#e6e6e6]"
            :style="{
              top: `${dividerTop[section.key] ?? (section.rhythm ?? RHYTHM) / 2}px`,
              transform: dividerOpen[section.key] ? 'scaleX(1)' : 'scaleX(0)',
              transitionProperty: 'transform',
              transitionDuration: `${DIVIDER_DRAW_MS}ms`,
              transitionTimingFunction: DIVIDER_DRAW_EASE,
            }"
          />
          <!-- --fit 은 제목이 줄임을 되돌리는 데 쓴다. 제목은 어느 해상도에서도
               같은 크기여야 해서 섹션이 줄어든 만큼 글자만 키운다. -->
          <div
            :ref="(el) => setFitEl(el, i)"
            :style="{ zoom: fitZoom[i], '--fit': fitZoom[i] }"
          >
            <component :is="section.component" v-bind="propsFor(section, i)" />
          </div>
        </div>

        <!-- 히어로 바닥의 "아래로 내려 보세요" 표시.
             인트로에서 히어로 글이 다 나온 뒤(4번 칸)에 같이 뜬다. -->
        <div
          v-if="section.key === 'hero'"
          class="pointer-events-none absolute bottom-[20px] left-1/2 z-10 -translate-x-1/2 transition-opacity duration-700"
          :class="frame >= 4 ? 'opacity-100' : 'opacity-0'"
        >
          <ScrollCue />
        </div>
      </div>
    </template>

  </div>

  <!-- 공통 상단. 스크롤을 따라다닌다.
       로그인 말풍선이 아래로 넘치므로 여기서 자르지 않는다.

       **배율 상자 밖이다**(2026-10-15 지시: 헤더는 그대로여야 한다).
       안에 두면 본문 배율(1440 에서 0.75)에 같이 줄어 135 -> 101, 글자 16 -> 12 가
       된다. CSS zoom 은 fixed 자식도 같이 줄이기 때문이다. 밖에 두면 어느 폭에서나
       135 실제px · 16px 그대로다.
       (2026-09-22 에도 밖에 뒀다가 안으로 넣었었다. 그때 이유는 "좁은 창에서
        헤더만 커 보인다" 였는데, 이제는 헤더 크기를 고정하는 것이 지시다.)

       대신 본문이 헤더에 가려지는 높이가 배율에 따라 달라진다 - headCanvas 와
       useFitHeight 의 headroom(실제 px)이 그걸 맡는다.

       높이를 고정하지 않는다. 시세표를 펼치면 헤더가 커지는데, 묶어 두면
       펼친 표가 상자를 넘어가 "떠 있는 레이어"로 보인다. -->
  <div ref="headerEl" class="fixed left-0 top-0 z-50 w-full">
    <div
      class="h-full will-change-transform [transition-property:transform,opacity] [transition-timing-function:cubic-bezier(0.33,1,0.68,1)]"
      :style="headerReveal"
    >
      <SiteHeader @rates-toggle="onRatesToggle" />
    </div>
  </div>
</template>
